import { Component, Injector, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AppConfiguration } from '../../../bootstrap/climatecare/app.configuration';
import { Cart } from '../../../shared/services/cart/cart.interface';
import { CartService } from '../../../shared/services/cart/cart.service';
import { CnAirMapService } from '../../../shared/services/cn-air-map/cn-air-map.service';
import { NavigationService } from '../../../shared/services/navigation/navigation.service';

import { FeatureComponent } from '../feature-component-registry/feature-component.interface';

const FFP_MEMBERSHIP_ENABLED = true;

@Component({
  selector: 'app-confirmation',
  templateUrl: './confirmation.component.html',
  styleUrls: ['./confirmation.component.css']
})
export class ConfirmationComponent implements OnInit, FeatureComponent {

  data: any;

  siteUrl: string;

  shareMessage: string;
  shareMessageHtml: string;

  tonnes: string;

  cart: Cart;

  requiresFfpMembership = false;

  ffpForm!: FormGroup;

  ffpSubmitting = false;

  ffpSubmitted = false;

  ffpError = false;

  constructor(private injector: Injector) {
    this.requiresFfpMembership = FFP_MEMBERSHIP_ENABLED && this.isClient('chinaair');

    this.injector.get(CartService).getCart().subscribe((cart: Cart) => {
      this.cart = cart;
      this.tonnes = cart.totalTonnes.toFixed(2);
    });

    if (!this.requiresFfpMembership) {
      return;
    }

    const ffpFieldValidators = [Validators.required, Validators.pattern(/^\S.*$/)];

    this.ffpForm = this.injector.get(FormBuilder).group({
      membershipNumber: [ '', ffpFieldValidators ],
      givenName: [ '', ffpFieldValidators ],
      familyName: [ '', ffpFieldValidators ],
      privacyConsent: [ false, Validators.requiredTrue ]
    });

    this.ffpForm.valueChanges.subscribe(form => {
      this.cart.membershipNumber = form.membershipNumber;
      this.cart.givenName = form.givenName;
      this.cart.familyName = form.familyName;
    });
  }

  ngOnInit(): void {
    const translateService = this.injector.get(TranslateService);
    const appConfiguration = this.injector.get(AppConfiguration);
    this.siteUrl = appConfiguration.clientConfiguration.clientSettings.shareUrl;
    translateService.get('confirmation.share-message').subscribe((res: string) => {
      this.shareMessageHtml = res + this.siteUrl;
      this.shareMessage = res;
    });
  }

  submitFfpMembership(): void {
    if (!this.requiresFfpMembership || !this.ffpForm.valid || this.ffpSubmitting) {
      return;
    }

    if (this.ffpError) {
      this.injector.get(TranslateService).get('confirmation.membership-retry-confirm-message')
        .subscribe((message: string) => {
          if (window.confirm(message)) {
            this.performFfpSubmission();
          }
        });
      return;
    }

    this.performFfpSubmission();
  }

  private performFfpSubmission(): void {
    this.ffpSubmitting = true;
    this.ffpSubmitted = false;
    this.ffpError = false;

    this.injector.get(CnAirMapService).createAccrualCarbonOffsetPoints({
      membershipNumber: this.cart.membershipNumber,
      givenName: this.cart.givenName,
      familyName: this.cart.familyName,
      billNumber: this.cart.billNumber!
    }).then(() => {
      this.ffpSubmitting = false;
      this.ffpSubmitted = true;
      this.ffpForm.disable();
      this.injector.get(CartService).regenerateBillNumber();
    }).catch((err) => {
      console.error('Failed to award China Airlines carbon offset points', err);
      this.ffpSubmitting = false;
      this.ffpError = true;
    });
  }

  back(): void {
    if (this.ffpSubmitted) {
      this.ffpForm.enable();
      this.ffpForm.reset();
      this.ffpSubmitted = false;
      this.ffpError = false;
    }

    this.injector.get(CartService).regenerateBillNumber();

    const navigationService = this.injector.get(NavigationService);
    navigationService.slideActiveTabStart({
      showNavigationMenu: true,
      showCheckout: false,
      showCurrencySelector: true,
      showDefaultHeader: true,
      isStart: true
    });
  }

  private isClient(client: string): boolean {
    let clientFound = false;

    const calcClient =
      this.injector.get(AppConfiguration).clientConfiguration.clientSettings.client;

    if (calcClient === client) {
      clientFound = true;
    }

    return clientFound;
  }

}
